import { ChatView } from '@/components/chat/ChatView'
import { ChatHeader } from '@/components/chat/ChatHeader'
import { ConversationSidebar } from '@/components/chat/ConversationSidebar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { useConversations } from '@/hooks/useConversations'
import { fetchWithAuth, AuthExpiredError } from '@/lib/fetchWithAuth'
import { useDashboardStore } from '@/store/dashboardStore'
import { useConversationStore } from '@/store/conversationStore'
import type { AgentResponse } from '@/types/api'
import { Loader2 } from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

interface Agent {
  agent_id: string
  name: string
  description?: string
  agent_type?: string
}

export function Dashboard() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const { user, logout } = useAuth()

  // Conversation state
  const {
    conversations,
    activeConversationId,
    setConversations,
    setActiveConversation,
    addConversation,
    removeConversation,
    updateConversation: updateConvInStore,
  } = useConversationStore()
  const {
    loadConversations,
    createConversation,
    deleteConversation,
    updateConversation,
    loadMessages,
  } = useConversations()

  // Dashboard store actions
  const { clearConversation, setMessages } = useDashboardStore()

  // Fetch available agents
  useEffect(() => {
    const fetchAgents = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await fetchWithAuth('/api/agents', {
          headers: { 'Content-Type': 'application/json' },
        })
        if (response.ok) {
          const data: AgentResponse[] = await response.json()
          const activeAgents: Agent[] = data
            .filter((agent) => agent.status === 'active')
            .map((agent) => ({
              agent_id: agent.agent_id,
              name: agent.name,
              description: agent.description || undefined,
              agent_type: agent.agent_type,
            }))

          // All active agents are selectable — SQL agents are user-facing data sources, not internal tools
          const visibleAgents = activeAgents

          if (visibleAgents.length > 0) {
            setAgents(visibleAgents)
            const orchestrator = visibleAgents.find((a) =>
              a.name.toLowerCase().includes('orchestrator')
            )
            setSelectedAgent(orchestrator ?? visibleAgents[0] ?? null)
          } else {
            setError('No active agents available. Please register agents in the Agent Management page.')
          }
        } else {
          setError(`Failed to fetch agents: ${response.status} ${response.statusText}`)
        }
      } catch (err) {
        if (err instanceof AuthExpiredError) return
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        setError(`Failed to fetch agents: ${errorMessage}`)
      } finally {
        setLoading(false)
      }
    }
    fetchAgents()
  }, [])

  // Load conversations on mount
  useEffect(() => {
    loadConversations()
      .then((list) => setConversations(list))
      .catch(() => {}) // non-fatal
  }, [loadConversations, setConversations])

  const handleNewChat = useCallback(async () => {
    try {
      const conv = await createConversation(selectedAgent?.agent_id, 'New conversation')
      addConversation(conv)
      setActiveConversation(conv.id)
      clearConversation()
    } catch {
      // silent — still clear and let user chat
      setActiveConversation(null)
      clearConversation()
    }
  }, [selectedAgent, createConversation, addConversation, setActiveConversation, clearConversation])

  const handleSelectConversation = useCallback(async (id: string) => {
    setActiveConversation(id)
    clearConversation()
    try {
      const msgs = await loadMessages(id)
      // Restore messages into dashboardStore
      const restored = msgs.map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        timestamp: new Date(m.created_at).getTime(),
        status: 'completed' as const,
        ...(m.content as Record<string, unknown>),
        blocks: (m.content as Record<string, unknown>).blocks ?? [],
        nodeExecutions: (m.content as Record<string, unknown>).nodeExecutions ?? [],
      }))
      setMessages(restored as Parameters<typeof setMessages>[0])
    } catch {
      // silent
    }
    // Update selected agent to match conversation's agent if possible
    const conv = conversations.find((c) => c.id === id)
    if (conv?.agent_id) {
      const matchingAgent = agents.find((a) => a.agent_id === conv.agent_id)
      if (matchingAgent) setSelectedAgent(matchingAgent)
    }
  }, [setActiveConversation, clearConversation, loadMessages, conversations, agents, setMessages])

  const handleDeleteConversation = useCallback(async (id: string) => {
    try {
      await deleteConversation(id)
      removeConversation(id)
      if (activeConversationId === id) {
        clearConversation()
      }
    } catch {
      // silent
    }
  }, [deleteConversation, removeConversation, activeConversationId, clearConversation])

  const handleAgentSelect = useCallback(async (agent: Agent) => {
    setSelectedAgent(agent)
    // If there's an active conversation, update its agent
    if (activeConversationId) {
      try {
        const updated = await updateConversation(activeConversationId, { agent_id: agent.agent_id })
        updateConvInStore(activeConversationId, { agent_id: updated.agent_id })
      } catch {
        // non-fatal
      }
    }
  }, [activeConversationId, updateConversation, updateConvInStore])

  const handleLogout = () => logout()

  if (loading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading agents...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/agents')}>
            Go to Agent Management
          </Button>
        </div>
      </div>
    )
  }

  if (agents.length === 0) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-muted-foreground mb-4">
            No active agents available. Please register agents in the Agent Management page.
          </p>
          <Button variant="outline" onClick={() => navigate('/agents')}>
            Go to Agent Management
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Left sidebar */}
      <ConversationSidebar
        open={sidebarOpen}
        conversations={conversations}
        activeConversationId={activeConversationId}
        onNewChat={handleNewChat}
        onSelectConversation={handleSelectConversation}
        onDeleteConversation={handleDeleteConversation}
        onToggle={() => setSidebarOpen((v) => !v)}
        onManageAgents={() => navigate('/agents')}
        username={user?.username}
      />

      {/* Main chat area */}
      <div className="flex flex-col flex-1 min-w-0">
        <ChatHeader
          agents={agents}
          selectedAgent={selectedAgent}
          onAgentSelect={handleAgentSelect}
          user={user}
          onLogout={handleLogout}
          onManageAgents={() => navigate('/agents')}
        />

        {selectedAgent && (
          <main className="flex-1 overflow-hidden">
            <ChatView
              agentId={selectedAgent.agent_id}
              agentName={selectedAgent.name}
              conversationId={activeConversationId}
              onConversationTitleUpdate={(id, title) => updateConvInStore(id, { title })}
            />
          </main>
        )}
      </div>
    </div>
  )
}
